<template>
  <div>
    <Head title="Sales Order Agents Summary Report" />
    <h1 class="mb-8 text-3xl font-bold">Sales Order Agents Summaries</h1>
    <div class="flex items-center justify-between mb-6">
      <date-range v-model="form.dateValue" class="mr-0 w-full max-w-md" @reset="reset"/>
    </div>
    <div class="flex items-center justify-between mb-6">
      <a
        v-show="(sales.data.length > 0 && url())"
        class="btn-orange px-3 py-2 text-xs cursor-pointer"
        :href="`/online-orders/sales-summaries/download${url()}`"
        download
        target="__blank"
      >
        <span>Download</span>
      </a>
    </div>
    <div class="w-10/12 lg:w-10/12 pb-2 pr-6">
      <div class="bg-white rounded-md shadow overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <tr class="text-left font-bold">
            <th class="pb-3 pt-3 px-3">Agent</th>
            <th class="pb-3 pt-3 items-end px-3 text-end justify-end float-right">Amount</th>
          </tr>
          <tr v-for="sale in sales.data" :key="sale.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
            <td class="border-t">
              <a class="flex items-center px-3 py-3 focus:text-orange-500" tabindex="-1" href="#" @click="openCategory(sale)">
                {{ sale.user }}
              </a>
            </td>
            <td class="border-t">
              <a class="flex items-end px-3 text-end justify-end" tabindex="-1"  href="#" @click="openCategory(sale)">
                {{ sale.amount }}
              </a>
            </td>
          </tr>
          <tr v-if="sales.data.length === 0">
            <td class="px-3 py-3 border-t" colspan="9">No data found</td>
          </tr>
        </table>
      </div>
      <pagination class="mt-6" :links="sales.links" />
    </div>
  </div>
  <div v-if="open" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-2/4" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
          <h1>
            <strong>Agent: {{ categoryObj.user }}</strong>
          </h1>
        </div>
        <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
          <div class="mb-4 overflow-x-auto">
            <table class="w-full whitespace-nowrap">
              <tr class="text-left font-bold">
                <th class="pb-3 pt-3 px-3">#</th>
                <th class="pb-3 pt-3 px-3">Category</th>
                <th class="pb-3 pt-3 items-end px-3 text-end justify-end float-right">Amount</th>
              </tr>
              <tr v-for="(item, index) in categoryObj.items" :key="index">
                <td class="w-px border-t pb-3 pt-3" width="2%">
                  <span class="flex items-center px-3">{{ index+1 }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3" width="10%">
                  <span class="flex items-left px-3">{{ item.category }}</span>
                </td>
                <td class="w-px border-t pb-3 pt-3">
                  <span class="flex items-end px-3 items-end justify-end">{{ item.amount }}</span>
                </td>
              </tr>
              <tr>
                <th class="pb-3 pt-3 border-t border-b" />
                <th class="pb-3 pt-3 px-3 border-t border-b">Total</th>
                <th class="pb-3 pt-3 border-t border-b">
                  <span class="flex items-end px-3 items-end justify-end">{{ categoryObj.amount }}</span>
                </th>
              </tr>
            </table>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
              Close
            </button>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import DateRange from '@/Shared/DateRange'
import Pagination from '@/Shared/Pagination'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'

export default {
  components: {
    Head,
    Link,
    Icon,
    DateRange,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    sales: Object,
  },
  data() {
    return {
      form: {
        dateValue: this.filters.dateValue,
      },
      categoryObj: null,
      open: false,
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/online-orders/sales-summaries', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openCategory(items) {
      this.open = true
      this.categoryObj = items
    },
    closeModal() {
      this.open = false
    },
    url() {
      let query = '';
      if (this.form.category) {
        query += `category=${this.form.category}`;
      }
      if (this.form.dateValue) {
        if (query) {
          query += '&';
        }
        query += `dateValue=${this.form.dateValue}`;
      }
      if (query) {
        query = `?${query}`;
      }
      return query;
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
